import {
  Alert,
  Box,
  Snackbar,
  Tabs,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "src/app/hooks"
import { thingsActions } from "src/features/things/thingsSlice"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import SaLayout from "./SaLayout"
import SaNotifDeliveriesEmail from "./SaNotifDeliveriesEmail"
import SaNotifDeliveriesEmailEvents from "./SaNotifDeliveriesEmailEvents"
import SaNotifDeliveriesSms from "./SaNotifDeliveriesSms"
import { Sl2Tab } from "./Sl2Tab"

const TabMap: Record<string, number> = {
  sms: 0,
  email: 1,
  emailEvents: 2,
}

const TabMapReverse: Record<number, string> = {
  0: "sms",
  1: "email",
  2: "emailEvents",
}

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaNotifDeliveriesPage: FC = () => {
  const { t } = useTranslation()

  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(thingsActions.clearSaList())
  }, [dispatch])

  const [tab, setTab] = useSearchParamsState("tab", TabMap.sms + "")

  const handleTabChange = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number) => {
      setTab(newValue + "", true)
      dispatch(thingsActions.clearSaList())
    },
    [dispatch, setTab],
  )

  return (
    <SaLayout selectedMenuItemLabelKey="notifDeliveries">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "primary.main",
            }}
          >
            {t("adminApp")}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            {t("notifDeliveries")}
          </Typography>
          <Typography
            sx={{
              mt: 1.25,
              color: "text.secondary",
              maxWidth: 720,
              lineHeight: 1.6,
              fontSize: "0.95rem",
            }}
          >
            {t("notifDeliveriesDescription")}
          </Typography>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            mb: 2.5,
          }}
        >
          <Tabs
            value={Number(tab)}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t("notifType")}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40, py: 1 },
            }}
          >
            <Sl2Tab label={t("sms")} />
            <Sl2Tab label={t("email")} />
            <Sl2Tab label={t("emailEvents2")} />
          </Tabs>
        </Box>

        {/* Content card */}
        <Box sx={cardSx}>
          {
            {
              sms: <SaNotifDeliveriesSms />,
              email: <SaNotifDeliveriesEmail />,
              emailEvents: <SaNotifDeliveriesEmailEvents />,
            }[TabMapReverse[Number(tab)]]
          }
        </Box>
      </Box>

      <Snackbar
        open={!!snackMessage?.message}
        autoHideDuration={6000}
        onClose={() => setSnackMessage({ message: "", severity: undefined })}
      >
        <Alert severity={snackMessage.severity}>{snackMessage.message}</Alert>
      </Snackbar>
    </SaLayout>
  )
}

export default SaNotifDeliveriesPage
